import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, TrendingUp, AlertCircle, CheckCircle, RotateCcw,
  ChevronRight, Trash2, Calculator, Languages, Target, XCircle,
  Download, Brain, Calendar, BarChart3, Clock, Sparkles
} from 'lucide-react';
import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface Mistake {
  id: number;
  questionId: number;
  wrongAnswer: string;
  reviewCount: number;
  createdAt: number;
  question: {
    id: number;
    topicId: number;
    type: string;
    difficulty: number;
    questionText: string;
    options?: string;
    correctAnswer: string;
    explanation?: string;
  };
}

interface TopicStat {
  topicId: number;
  count: number;
  topic: {
    id: number;
    name: string;
    nameCn: string;
    subjectId: number;
  } | null;
  subject: {
    id: number;
    name: string;
    nameCn: string;
  } | null;
}

interface Stats {
  byTopic: TopicStat[];
  totalMistakes: number;
  totalPractice: number;
  correctRate: number;
}

interface Recommendation {
  id: number;
  questionId: number;
  wrongAnswer: string;
  reviewCount: number;
  priorityScore: number;
  question: {
    id: number;
    topicId: number;
    type: string;
    difficulty: number;
    questionText: string;
    options?: string;
    correctAnswer: string;
    explanation?: string;
  };
  topic: { id: number; name: string; nameCn: string } | null;
  subject: { id: number; name: string; nameCn: string } | null;
}

interface DailyProgress {
  date: string;
  total: number;
  correct: number;
  rate: number;
}

interface SubjectProgress {
  subject: { id: number; name: string; nameCn: string };
  total: number;
  correct: number;
  rate: number;
}

type TabType = 'mistakes' | 'recommendations' | 'progress';

export function MistakesPage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('mistakes');
  const [mistakes, setMistakes] = useState<Mistake[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [dailyProgress, setDailyProgress] = useState<DailyProgress[]>([]);
  const [subjectProgress, setSubjectProgress] = useState<SubjectProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'math' | 'english'>('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  async function loadData() {
    setLoading(true);
    try {
      const [mistakesRes, statsRes, recommendationsRes, dailyRes, subjectRes] = await Promise.all([
        client.api.fetch('/api/mistakes'),
        client.api.fetch('/api/mistakes/stats'),
        client.api.fetch('/api/review/recommendations?limit=10'),
        client.api.fetch('/api/progress/daily?days=14'),
        client.api.fetch('/api/progress/by-subject'),
      ]);
      
      const mistakesData = await mistakesRes.json();
      const statsData = await statsRes.json();
      const recommendationsData = await recommendationsRes.json();
      const dailyData = await dailyRes.json();
      const subjectData = await subjectRes.json();
      
      setMistakes(mistakesData.data || []);
      setStats(statsData.data || null);
      setRecommendations(recommendationsData.data || []);
      setDailyProgress(dailyData.data || []);
      setSubjectProgress(subjectData.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkMastered(mistakeId: number) {
    try {
      await client.api.fetch(`/api/mistakes/${mistakeId}`, { method: 'DELETE' });
      setMistakes(prev => prev.filter(m => m.id !== mistakeId));
      setRecommendations(prev => prev.filter(r => r.id !== mistakeId));
      // Refresh stats
      const statsRes = await client.api.fetch('/api/mistakes/stats');
      const statsData = await statsRes.json();
      setStats(statsData.data);
    } catch (error) {
      console.error('Failed to mark as mastered:', error);
    }
  }

  async function handleReview(mistakeId: number) {
    try {
      await client.api.fetch(`/api/mistakes/${mistakeId}/review`, { method: 'PUT' });
      setMistakes(prev => 
        prev.map(m => m.id === mistakeId ? { ...m, reviewCount: m.reviewCount + 1 } : m)
      );
      setRecommendations(prev =>
        prev.map(r => r.id === mistakeId ? { ...r, reviewCount: r.reviewCount + 1 } : r)
      );
    } catch (error) {
      console.error('Failed to record review:', error);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const res = await client.api.fetch('/api/mistakes/export');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `错题本_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export:', error);
    } finally {
      setExporting(false);
    }
  }

  function parseOptions(optionsStr?: string): string[] {
    if (!optionsStr) return [];
    try {
      return JSON.parse(optionsStr);
    } catch {
      return [];
    }
  }

  function getSubjectIcon(subjectName?: string) {
    if (subjectName?.toLowerCase() === 'mathematics') {
      return <Calculator className="w-4 h-4" />;
    }
    return <Languages className="w-4 h-4" />;
  }

  function getSubjectColor(subjectName?: string) {
    if (subjectName?.toLowerCase() === 'mathematics') {
      return { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', gradient: 'from-blue-500 to-indigo-500' };
    }
    return { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', gradient: 'from-emerald-500 to-teal-500' };
  }

  // Get max value for chart scaling
  const maxDailyTotal = Math.max(...dailyProgress.map(d => d.total), 1);

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">请先登录</h2>
          <p className="text-slate-500 mb-4">登录后即可查看错题本</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            去登录
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">错题本</h1>
            <p className="text-slate-600">分析薄弱环节，智能推荐复习，追踪学习进度</p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || mistakes.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {exporting ? '导出中...' : '导出错题'}
          </button>
        </motion.div>

        {/* Stats Overview */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          >
            <div className="bg-white rounded-2xl p-5 border border-slate-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
                <span className="text-sm text-slate-500">错题总数</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">{stats.totalMistakes}</div>
            </div>
            
            <div className="bg-white rounded-2xl p-5 border border-slate-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                </div>
                <span className="text-sm text-slate-500">练习总数</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">{stats.totalPractice}</div>
            </div>
            
            <div className="bg-white rounded-2xl p-5 border border-slate-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <span className="text-sm text-slate-500">正确率</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">{stats.correctRate}%</div>
            </div>
            
            <div className="bg-white rounded-2xl p-5 border border-slate-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-amber-500" />
                </div>
                <span className="text-sm text-slate-500">薄弱知识点</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">{stats.byTopic.length}</div>
            </div>
          </motion.div>
        )}

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex gap-2 mb-6 bg-white rounded-xl p-1.5 border border-slate-100 w-fit"
        >
          {[
            { id: 'mistakes' as TabType, label: '错题列表', icon: BookOpen },
            { id: 'recommendations' as TabType, label: '智能推荐', icon: Brain },
            { id: 'progress' as TabType, label: '学习进度', icon: BarChart3 },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'mistakes' && (
            <motion.div
              key="mistakes"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Weakness Analysis */}
              {stats && stats.byTopic.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border border-slate-100 mb-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-500" />
                    薄弱环节分析
                  </h2>
                  <div className="space-y-3">
                    {stats.byTopic
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 5)
                      .map((stat, index) => {
                        const colors = getSubjectColor(stat.subject?.name);
                        const percentage = Math.round((stat.count / stats.totalMistakes) * 100);
                        
                        return (
                          <div key={stat.topicId} className="flex items-center gap-4">
                            <div className="w-6 text-sm font-medium text-slate-400">
                              #{index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${colors.bg} ${colors.text}`}>
                                    {getSubjectIcon(stat.subject?.name)}
                                    {stat.subject?.nameCn}
                                  </span>
                                  <span className="font-medium text-slate-800">
                                    {stat.topic?.nameCn || '未知知识点'}
                                  </span>
                                </div>
                                <span className="text-sm text-slate-500">
                                  {stat.count} 题 ({percentage}%)
                                </span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Mistakes List */}
              <div className="bg-white rounded-2xl border border-slate-100">
                <div className="p-6 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-slate-900">错题列表</h2>
                    <div className="flex gap-2">
                      {['all', 'math', 'english'].map((f) => (
                        <button
                          key={f}
                          onClick={() => setFilter(f as any)}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            filter === f
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {f === 'all' ? '全部' : f === 'math' ? '数学' : '英语'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {mistakes.length === 0 ? (
                  <div className="p-12 text-center">
                    <CheckCircle className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">太棒了！</h3>
                    <p className="text-slate-500">目前没有错题，继续保持！</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                    {mistakes.map((mistake, index) => (
                      <MistakeCard
                        key={mistake.id}
                        mistake={mistake}
                        index={index}
                        onReview={handleReview}
                        onMarkMastered={handleMarkMastered}
                        parseOptions={parseOptions}
                      />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'recommendations' && (
            <motion.div
              key="recommendations"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 text-white mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-semibold">智能复习推荐</h2>
                </div>
                <p className="text-white/80 text-sm">
                  基于遗忘曲线算法，智能推荐需要复习的错题。优先复习长时间未复习、难度较高的题目。
                </p>
              </div>

              {recommendations.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
                  <CheckCircle className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">暂无推荐</h3>
                  <p className="text-slate-500">你的错题都已复习完毕，继续保持！</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations.map((rec, index) => {
                    const colors = getSubjectColor(rec.subject?.name);
                    const options = parseOptions(rec.question.options);
                    
                    return (
                      <motion.div
                        key={rec.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white rounded-2xl p-5 border border-slate-100"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r ${colors.gradient} text-white`}>
                                优先级 {rec.priorityScore}
                              </span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${colors.bg} ${colors.text}`}>
                                {getSubjectIcon(rec.subject?.name)}
                                {rec.subject?.nameCn}
                              </span>
                              <span className="text-xs text-slate-500">
                                {rec.topic?.nameCn}
                              </span>
                            </div>
                            
                            <p className="text-slate-800 mb-3">{rec.question.questionText}</p>
                            
                            {rec.question.type === 'multiple_choice' && options.length > 0 && (
                              <div className="space-y-1.5 mb-3">
                                {options.map((opt, i) => {
                                  const letter = String.fromCharCode(65 + i);
                                  const isCorrect = letter === rec.question.correctAnswer;
                                  const isWrong = letter === rec.wrongAnswer;
                                  
                                  return (
                                    <div
                                      key={i}
                                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                                        isCorrect ? 'bg-green-50 text-green-700' :
                                        isWrong ? 'bg-red-50 text-red-700' :
                                        'bg-slate-50 text-slate-600'
                                      }`}
                                    >
                                      <span className="font-medium">{letter}.</span>
                                      <span>{opt}</span>
                                      {isCorrect && <CheckCircle className="w-4 h-4 ml-auto" />}
                                      {isWrong && <XCircle className="w-4 h-4 ml-auto" />}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {rec.question.explanation && (
                              <div className="p-3 bg-blue-50 rounded-lg">
                                <div className="text-sm text-blue-700">
                                  <span className="font-medium">解析：</span>
                                  {rec.question.explanation}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleReview(rec.id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="标记为已复习"
                            >
                              <RotateCcw className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleMarkMastered(rec.id)}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="标记为已掌握"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'progress' && (
            <motion.div
              key="progress"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Daily Progress Chart */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  每日练习统计（近14天）
                </h2>
                
                {dailyProgress.length === 0 || dailyProgress.every(d => d.total === 0) ? (
                  <div className="text-center py-8 text-slate-500">
                    暂无练习记录，开始练习后数据将在这里显示
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Chart */}
                    <div className="flex items-end gap-1 h-32">
                      {dailyProgress.map((day, index) => {
                        const height = day.total > 0 ? Math.max((day.total / maxDailyTotal) * 100, 10) : 0;
                        const correctHeight = day.total > 0 ? (day.correct / day.total) * height : 0;
                        
                        return (
                          <div
                            key={day.date}
                            className="flex-1 flex flex-col justify-end items-center group relative"
                          >
                            {/* Tooltip */}
                            <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                              <div className="bg-slate-900 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap">
                                {day.date.slice(5)} | {day.total}题 | {day.rate}%
                              </div>
                            </div>
                            
                            {/* Bar */}
                            <div 
                              className="w-full rounded-t bg-slate-200 relative overflow-hidden transition-all"
                              style={{ height: `${height}%`, minHeight: day.total > 0 ? '4px' : '0' }}
                            >
                              <div
                                className="absolute bottom-0 w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t"
                                style={{ height: `${day.total > 0 ? (day.correct / day.total) * 100 : 0}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Date labels */}
                    <div className="flex gap-1 text-[10px] text-slate-400">
                      {dailyProgress.map((day, index) => (
                        <div key={day.date} className="flex-1 text-center">
                          {index % 2 === 0 ? day.date.slice(5) : ''}
                        </div>
                      ))}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex items-center justify-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-slate-200" />
                        <span className="text-slate-500">总题数</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-blue-500" />
                        <span className="text-slate-500">正确数</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Subject Progress */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-500" />
                  分科目统计
                </h2>
                
                {subjectProgress.length === 0 || subjectProgress.every(s => s.total === 0) ? (
                  <div className="text-center py-8 text-slate-500">
                    暂无练习记录
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {subjectProgress.map(sp => {
                      const colors = getSubjectColor(sp.subject.name);
                      
                      return (
                        <div
                          key={sp.subject.id}
                          className={`rounded-xl p-4 ${colors.bg} border ${colors.border}`}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.text} bg-white/60`}>
                              {getSubjectIcon(sp.subject.name)}
                            </div>
                            <span className={`font-semibold ${colors.text}`}>{sp.subject.nameCn}</span>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className={colors.text}>练习题数</span>
                              <span className="font-medium text-slate-700">{sp.total}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className={colors.text}>正确数</span>
                              <span className="font-medium text-slate-700">{sp.correct}</span>
                            </div>
                            <div className="h-2 bg-white/60 rounded-full overflow-hidden">
                              <div
                                className={`h-full bg-gradient-to-r ${colors.gradient} rounded-full`}
                                style={{ width: `${sp.rate}%` }}
                              />
                            </div>
                            <div className="text-right">
                              <span className={`text-lg font-bold ${colors.text}`}>{sp.rate}%</span>
                              <span className={`text-sm ml-1 ${colors.text} opacity-70`}>正确率</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Tips */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100">
                <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  学习小贴士
                </h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• 每天坚持练习20-30分钟效果最佳</li>
                  <li>• 错题建议间隔1-3天后复习，巩固记忆</li>
                  <li>• 关注薄弱知识点，针对性突破</li>
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Separate component for mistake card to reduce complexity
function MistakeCard({ 
  mistake, 
  index, 
  onReview, 
  onMarkMastered,
  parseOptions 
}: { 
  mistake: Mistake; 
  index: number; 
  onReview: (id: number) => void;
  onMarkMastered: (id: number) => void;
  parseOptions: (str?: string) => string[];
}) {
  const options = parseOptions(mistake.question.options);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.03 }}
      className="p-4 hover:bg-slate-50 transition-colors"
    >
      <div className="flex items-start gap-4">
        <div className="flex-1">
          {/* Question header */}
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              mistake.question.difficulty <= 2 ? 'bg-green-100 text-green-700' :
              mistake.question.difficulty <= 3 ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              难度 {mistake.question.difficulty}
            </span>
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
              {mistake.question.type === 'multiple_choice' ? '选择题' : '填空题'}
            </span>
            {mistake.reviewCount > 0 && (
              <span className="px-2 py-0.5 bg-blue-50 rounded text-xs text-blue-600">
                已复习 {mistake.reviewCount} 次
              </span>
            )}
          </div>

          {/* Question text */}
          <p className="text-slate-800 mb-3 leading-relaxed">
            {mistake.question.questionText}
          </p>

          {/* Options for multiple choice */}
          {mistake.question.type === 'multiple_choice' && options.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {options.map((opt, i) => {
                const letter = String.fromCharCode(65 + i);
                const isCorrect = letter === mistake.question.correctAnswer;
                const isWrong = letter === mistake.wrongAnswer;
                
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      isCorrect ? 'bg-green-50 text-green-700' :
                      isWrong ? 'bg-red-50 text-red-700' :
                      'bg-slate-50 text-slate-600'
                    }`}
                  >
                    <span className="font-medium">{letter}.</span>
                    <span>{opt}</span>
                    {isCorrect && <CheckCircle className="w-4 h-4 ml-auto" />}
                    {isWrong && <XCircle className="w-4 h-4 ml-auto" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Answer summary */}
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-500">
              你的答案: <span className="text-red-600 font-medium">{mistake.wrongAnswer}</span>
            </span>
            <span className="text-slate-500">
              正确答案: <span className="text-green-600 font-medium">{mistake.question.correctAnswer}</span>
            </span>
          </div>

          {/* Explanation */}
          {mistake.question.explanation && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-700">
                <span className="font-medium">解析：</span>
                {mistake.question.explanation}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => onReview(mistake.id)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="标记为已复习"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <button
            onClick={() => onMarkMastered(mistake.id)}
            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            title="标记为已掌握"
          >
            <CheckCircle className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
